import {
  EXTERNAL_DECISION_STATUS,
  TASK_PRIORITY,
  TASK_STATUS,
  TASK_TYPE,
  TChangePlan,
  TPlanTask,
  TWorkPackage,
} from '../models';
import { buildWorkPackages, workPackageForType } from '../utils/planEngine';

// A fully populated execution plan for the supplier/component demo. It seeds
// Screen 8 (execution cockpit) with the states described in the prompt so the
// register shows a realistic in-flight change without walking the whole flow.

const iso = (date: string): string => `${date}T00:00:00.000Z`;

const wp = (type: TPlanTask['taskType']): string => workPackageForType(type);

let demoCounter = 0;
const makeTask = (partial: Partial<TPlanTask> & Pick<TPlanTask, 'title' | 'taskType' | 'status'>): TPlanTask => {
  demoCounter += 1;
  return {
    id: `demo-task-${demoCounter}`,
    workPackageId: wp(partial.taskType),
    contributors: [],
    priority: TASK_PRIORITY.NORMAL,
    dependencies: [],
    required: true,
    origin: 'Assessment',
    ...partial,
  };
};

export const buildDemoExecutionPlan = (): TChangePlan => {
  demoCounter = 0;
  const workPackages: TWorkPackage[] = buildWorkPackages();

  const tasks: TPlanTask[] = [
    makeTask({
      title: 'Qualify Aurelia MedTech and approve the quality agreement',
      taskType: TASK_TYPE.SUPPLIER_QUALIFICATION,
      status: TASK_STATUS.COMPLETE,
      owner: 'Michael Fischer',
      responsibleFunction: 'Supplier quality',
      dueDate: iso('2026-07-10'),
      completionCriteria: 'Supplier qualified and quality agreement approved.',
      linkedObject: 'SUP-118',
      completedBy: 'Michael Fischer',
      completedAt: iso('2026-07-09'),
    }),
    makeTask({
      title: 'Update component specifications, device description and bill of materials',
      taskType: TASK_TYPE.DOCUMENT_REVISION,
      status: TASK_STATUS.IN_REVIEW,
      owner: 'Martin Vogt',
      responsibleFunction: 'Engineering or R&D',
      dueDate: iso('2026-07-25'),
      completionCriteria: 'All affected documents approved.',
      linkedObject: 'SPEC-033',
      documents: [
        {
          _id: '6a4a475a1f350133d5fb33af',
          name: 'Device Description & Specification',
        },
      ],
    }),
    makeTask({
      title: 'Update the risk management file',
      taskType: TASK_TYPE.RISK_MANAGEMENT,
      status: TASK_STATUS.COMPLETE,
      owner: 'Martin Vogt',
      responsibleFunction: 'Engineering or R&D',
      dueDate: iso('2026-07-18'),
      completionCriteria: 'Updated risk management file approved.',
      linkedObject: 'RMF-001',
      completedBy: 'Martin Vogt',
      completedAt: iso('2026-07-17'),
    }),
    makeTask({
      title: 'Update the biological evaluation',
      taskType: TASK_TYPE.BIOLOGICAL_EVALUATION,
      status: TASK_STATUS.IN_PROGRESS,
      owner: 'Dr. Sofia Meier',
      responsibleFunction: 'Biological safety',
      dueDate: iso('2026-07-30'),
      completionCriteria: 'Updated biological evaluation approved.',
    }),
    makeTask({
      title: 'Prepare and execute comparative verification',
      taskType: TASK_TYPE.VERIFICATION_VALIDATION,
      status: TASK_STATUS.IN_PROGRESS,
      priority: TASK_PRIORITY.HIGH,
      owner: 'Martin Vogt',
      responsibleFunction: 'Engineering or R&D',
      dueDate: iso('2026-08-10'),
      completionCriteria: 'Acceptance criteria met and the report is approved.',
      linkedObject: 'DVR-207',
    }),
    makeTask({
      title: 'Complete the IFU, UDI and regulatory impact review',
      taskType: TASK_TYPE.REGULATORY_ASSESSMENT,
      status: TASK_STATUS.NOT_STARTED,
      owner: 'Elena Rossi',
      responsibleFunction: 'Regulatory affairs',
      dueDate: iso('2026-08-20'),
      completionCriteria: 'IFU, UDI and regulatory impact confirmed.',
      linkedObject: 'IFU-014',
    }),
    makeTask({
      title: 'Define inventory disposition and production cutover',
      taskType: TASK_TYPE.PRODUCTION_CUTOVER,
      status: TASK_STATUS.NOT_STARTED,
      owner: 'Thomas Bauer',
      responsibleFunction: 'Production or operations',
      dueDate: iso('2026-08-28'),
      completionCriteria: 'Inventory disposition and cutover date approved.',
    }),
    // External notified-body activity - an external gate, never an internal approval.
    makeTask({
      title: 'Obtain notified-body impact decision',
      taskType: TASK_TYPE.EXTERNAL_DECISION,
      status: TASK_STATUS.NOT_STARTED,
      owner: 'Elena Rossi',
      responsibleFunction: 'Regulatory affairs',
      dueDate: iso('2026-08-15'),
      description: 'Notified-body impact: assessment pending. No decision yet.',
      external: true,
      externalStatus: EXTERNAL_DECISION_STATUS.ASSESSMENT_PENDING,
      releaseBlocking: true,
      origin: 'Automation',
      automationRule: 'External notification or approval required → external decision required before release.',
    }),
  ];

  return {
    workPackages,
    tasks,
    targetDate: iso('2026-09-01'),
    reportedImpacts: [],
    reassessmentRequired: false,
    baseline: {
      version: 1,
      frozenAt: iso('2026-07-08'),
      frozenBy: 'Lukas Poterpin',
      taskCount: tasks.length,
      workPackageCount: workPackages.length,
      targetDate: iso('2026-09-01'),
    },
  };
};

// A compact completed plan used by Completion/Closed mock records.
export const buildDemoCompletedPlan = (): TChangePlan => {
  const plan = buildDemoExecutionPlan();
  const completedAt = iso('2026-07-14');
  return {
    ...plan,
    tasks: plan.tasks.map((task) => ({
      ...task,
      status: TASK_STATUS.COMPLETE,
      externalStatus: task.external ? EXTERNAL_DECISION_STATUS.DECISION_RECEIVED : task.externalStatus,
      completedBy: task.completedBy ?? 'Lukas Poterpin',
      completedAt: task.completedAt ?? completedAt,
    })),
  };
};
